import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FolderKanban,
  Plus,
  Bell,
  Settings,
  Search,
  ArrowRight,
  Heart,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Film,
  Youtube,
  Eye,
  RotateCcw,
  Sparkles,
  Lock,
  Crown,
  Unlink,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { fetchCreationConfig } from '@/lib/creationPlan';
import { subscribeRazorpayMessages, syncUserCredits } from '@/lib/billing/razorpayCheckout';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import { STORY_FLOWS } from '@/data/storyFlows';
import { TwinneeWordmark } from '@/components/TwinneeWordmark';
import { DashboardCreateStoryHero } from '@/components/DashboardCreateStoryHero';
import { YouTubePublishDialog } from '@/components/YouTubePublishDialog';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useChannelOverview, WhatToMakeNext, YouTubeHighlights } from '@/components/channel/ChannelPerformanceBand';
import { GeneralTrendsBrief } from '@/components/channel/GeneralTrendsBrief';

/* ─────────────────────────────────────────
   Creation Flows Category Mapping
───────────────────────────────────────── */
const FLOW_CATEGORIES = [
  { id: 'all', label: 'All Formats' },
  { id: 'stories', label: 'Story & Scripts' },
  { id: 'avatars', label: 'Avatars & Video' },
  { id: 'music', label: 'Music & Audio' },
  { id: 'learning', label: 'Tutorials & Tree' },
  { id: 'marketing', label: 'Marketing & Ads' },
];

const FLOW_CATEGORY_MAP = {
  avatar_cast: 'avatars',
  narration: 'stories',
  script_based: 'stories',
  storybuilder: 'stories',
  reels_shorts: 'marketing',
  song_based: 'music',
  tutorial: 'learning',
  tutorial_v3: 'learning',
  tutorial_v4: 'learning',
  learning: 'learning',
  pitch_recording: 'marketing',
  news_video: 'avatars',
  mascot_animation: 'avatars',
  advertisement: 'marketing',
  dialogue: 'stories',
};

/* ─────────────────────────────────────────
   Gallery Category Filter Pills
───────────────────────────────────────── */
const RECREATE_BUTTON_CLASS =
  'flex-1 h-7 rounded-lg border border-white/30 text-white text-[11px] font-semibold inline-flex items-center justify-center gap-1 cursor-pointer backdrop-blur-md bg-gradient-to-r from-[#c4b5fd]/55 via-[#a78bfa]/35 to-[#67e8f9]/50 shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] hover:from-[#c4b5fd]/75 hover:via-[#a78bfa]/55 hover:to-[#67e8f9]/70 transition-all [text-shadow:0_1px_1px_rgba(0,0,0,0.35)]';

const GALLERY_TABS = [
  { id: 'recommended', label: 'Recommended' },
  { id: 'follows', label: 'Follows' },
  { id: 'shorts', label: 'Shorts' },
  { id: 'your_projects', label: 'Your Projects', isGradient: true },
];

/** Normalize aspect values like "16:9", "16/9", or 1.777 → CSS aspect-ratio. */
function galleryAspectCss(value) {
  if (value == null || value === '') return '3 / 4';
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return String(Math.min(2.4, Math.max(0.45, value)));
  }
  const raw = String(value).trim().replace(':', '/');
  const m = raw.match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/);
  if (m) {
    const w = Number(m[1]);
    const h = Number(m[2]);
    if (w > 0 && h > 0) {
      const r = w / h;
      if (r > 2.4) return '21 / 9';
      if (r < 0.45) return '9 / 21';
      return `${w} / ${h}`;
    }
  }
  return '3 / 4';
}

/** Tab / tag heuristics before media metadata loads. */
function inferGalleryAspect(item, tabId) {
  const tag = String(item?.tag || '').toLowerCase();
  const title = String(item?.title || '').toLowerCase();
  if (tabId === 'shorts' || tag.includes('short') || title.includes('9:16')) return '9 / 16';
  if (tabId === '4k' || tag.includes('4k') || title.includes('landscape') || title.includes('16:9')) {
    return '16 / 9';
  }
  if (tag.includes('portrait') || title.includes('portrait')) return '3 / 4';
  if (tag.includes('square') || title.includes('1:1')) return '1 / 1';
  // Stable variety from id so masonry staggers before media loads
  const seed = String(item?.id || item?.title || 'x')
    .split('')
    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const presets = ['3 / 4', '4 / 5', '2 / 3', '1 / 1', '16 / 9', '9 / 16', '5 / 4'];
  return presets[seed % presets.length];
}

/** Stable masonry stagger for Your Projects before preview media loads. */
function inferProjectAspect(task) {
  const seed = String(task?.id || task?.title || 'x')
    .split('')
    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  // Mix portrait / landscape so the column layout staggers like gallery tabs
  const presets = ['16 / 9', '3 / 4', '4 / 5', '1 / 1', '9 / 16', '2 / 3', '5 / 4'];
  return presets[seed % presets.length];
}

/** Exported / preview video URL for hover playback (if any). */
function resolveExportedVideoUrl(itemOrTask) {
  const raw =
    itemOrTask?.export_video_url ||
    itemOrTask?.video_url ||
    itemOrTask?.preview_video_url ||
    itemOrTask?.preview_video_s3_url ||
    itemOrTask?.final_video_url ||
    '';
  const resolved = mediaUrlForPlayback(raw, {
    s3Url: itemOrTask?.video_s3_url || itemOrTask?.preview_video_s3_url || itemOrTask?.final_video_s3_url,
    localUrl: itemOrTask?.video_local_url || itemOrTask?.preview_video_local_url || itemOrTask?.final_video_local_url,
  });
  return resolved || (raw && String(raw).startsWith('http') ? raw : '') || '';
}

function startHoverVideo(container) {
  const video = container?.querySelector?.('video[data-hover-preview]');
  if (!video) return;
  const src = video.dataset.src || '';
  if (!src) return;
  if (video.getAttribute('src') !== src) {
    video.src = src;
    try {
      video.load();
    } catch {
      /* ignore */
    }
  }
  video.muted = true;
  video.playsInline = true;
  const tryPlay = () => {
    video.currentTime = 0;
    const p = video.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };
  if (video.readyState >= 2) tryPlay();
  else video.addEventListener('loadeddata', tryPlay, { once: true });
}

function stopHoverVideo(container) {
  const video = container?.querySelector?.('video[data-hover-preview]');
  if (!video) return;
  try {
    video.pause();
    video.currentTime = 0;
  } catch {
    /* ignore */
  }
}

export const DashboardPage = () => {
  const navigate = useNavigate();
  const [activeNav, setActiveNav] = useState('home');
  const [activeHeaderTab, setActiveHeaderTab] = useState('gallery');
  const [activeGalleryTab, setActiveGalleryTab] = useState('recommended');
  const [activeFlowCategory, setActiveFlowCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [unifiedOnly, setUnifiedOnly] = useState(false);

  // Real user projects and notifications from backend
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ stories: 0, characters: 0, total_jobs: 0, completed_jobs: 0 });
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [likingKey, setLikingKey] = useState(null);
  const [recreateRequest, setRecreateRequest] = useState(null);
  const [youtubePublish, setYoutubePublish] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const notifRef = useRef(null);
  const flowsScrollRef = useRef(null);

  const scrollFlows = (direction) => {
    if (flowsScrollRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      flowsScrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const user = JSON.parse(localStorage.getItem('storycraft_user') || '{}');
  const displayName = user.name?.split(' ')?.[0] || 'Creator';
  const [youtubeFormat, setYoutubeFormat] = useState('all');
  const [advancedUnlocked, setAdvancedUnlocked] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('storycraft_user') || '{}');
      return Boolean(stored.advanced_analytics || stored.account_paid);
    } catch {
      return false;
    }
  });
  const channelHome = useChannelOverview('28d', '', '', '/dashboard', youtubeFormat);

  useEffect(() => {
    let cancelled = false;
    fetchCreationConfig()
      .then((config) => {
        if (!cancelled) setUnifiedOnly(config.unifiedOnly);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadAccount = () => {
      api.get('/billing/wallet')
        .then(({ data }) => {
          if (cancelled) return;
          setAdvancedUnlocked(Boolean(data?.advanced_analytics || data?.paid));
          if (typeof data?.balance === 'number') syncUserCredits(data.balance, data);
        })
        .catch(() => {
          /* A reload blip must not put the lock back on a paid account. */
        });
    };
    loadAccount();
    window.addEventListener('focus', loadAccount);
    const unsubscribe = subscribeRazorpayMessages({ onSuccess: loadAccount });
    return () => {
      cancelled = true;
      window.removeEventListener('focus', loadAccount);
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    loadData();
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activeGalleryTab === 'your_projects') return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setGalleryLoading(true);
      try {
        const { data } = await api.get('/gallery/inspiration', {
          params: {
            tab: activeGalleryTab,
            q: searchQuery.trim() || undefined,
            limit: 48,
          },
        });
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        setGalleryItems(
          items.map((item) => {
            const video_url =
              mediaUrlForPlayback(item.video_url, {
                s3Url: item.video_s3_url,
                localUrl: item.video_local_url,
              }) ||
              (item.video_url && String(item.video_url).startsWith('http') ? item.video_url : '') ||
              '';
            return {
              ...item,
              image_url:
                mediaUrlForPlayback(item.image_url, {
                  s3Url: item.image_s3_url,
                  localUrl: item.image_local_url,
                }) || item.image_url,
              video_url,
              has_exported_video: Boolean(item.has_exported_video || video_url),
              likes_count: item.likes_label || String(item.likes_count ?? 0),
            };
          })
        );
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load gallery:', err);
          setGalleryItems([]);
        }
      } finally {
        if (!cancelled) setGalleryLoading(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [activeGalleryTab, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem('storycraft_token');
      if (!token) return;
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(backendUrl + '/api/notifications', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data || []);
      }
    } catch {
      /* ignore */
    }
  };

  const getTaskPreview = (task) => {
    const rawImg =
      task.preview_image_url ||
      task.preview_image_s3_url ||
      task.preview_image_local_url ||
      task.thumbnail_url ||
      task.preview_image;
    if (rawImg) return { type: 'image', url: mediaUrlForPlayback(rawImg) };
    const rawVid =
      task.preview_video_url ||
      task.preview_video_s3_url ||
      task.preview_video_local_url ||
      task.video_url;
    if (rawVid) return { type: 'video', url: mediaUrlForPlayback(rawVid) };
    return null;
  };

  const getTaskExportedVideo = (task) => resolveExportedVideoUrl(task);

  const getStatusBadge = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'completed':
      case 'generated':
        return { label: 'Ready', bg: 'bg-[#10B981]/15 text-[#34d399] border-[#10B981]/30' };
      case 'generating':
      case 'running':
      case 'pending':
        return { label: 'Generating', bg: 'bg-[#6366F1]/15 text-[#a5b4fc] border-[#6366F1]/30' };
      default:
        return { label: 'Draft', bg: 'bg-white/10 text-gray-300 border-white/10' };
    }
  };

  const loadData = async () => {
    try {
      const [tasksRes, statsRes] = await Promise.allSettled([
        api.get('/dashboard/tasks'),
        api.get('/dashboard/stats'),
      ]);
      if (tasksRes.status === 'fulfilled') {
        setTasks(Array.isArray(tasksRes.value?.data?.tasks) ? tasksRes.value.data.tasks : []);
      }
      if (statsRes.status === 'fulfilled' && statsRes.value?.data) {
        setStats(statsRes.value.data);
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (task, e) => {
    e.stopPropagation();
    try {
      if (task.kind === 'narration') {
        await api.delete(`/projects/${task.id}`);
      } else {
        await api.delete(`/stories/${task.id}`);
      }
      setTasks((prev) => prev.filter((t) => !(t.kind === task.kind && t.id === task.id)));
      toast.success('Project removed');
    } catch {
      toast.error('Failed to delete project');
    }
  };

  const toggleGalleryLike = async (item, e) => {
    e?.stopPropagation?.();
    if (!item?.id || !item?.kind || item.is_owner) return;
    const key = `${item.kind}:${item.id}`;
    if (likingKey === key) return;
    setLikingKey(key);
    try {
      const { data } = await api.post(`/gallery/works/${item.kind}/${item.id}/like`);
      setGalleryItems((prev) =>
        prev.map((row) =>
          row.id === item.id && row.kind === item.kind
            ? {
                ...row,
                liked: Boolean(data?.liked),
                likes_count: data?.likes_label || String(data?.likes_count ?? row.likes_count),
                likes_label: data?.likes_label,
              }
            : row
        )
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update like');
    } finally {
      setLikingKey(null);
    }
  };

  const openRecreate = (item, e) => {
    e?.stopPropagation?.();
    const prompt = String(item?.prompt || item?.title || '').trim();
    if (!prompt) {
      toast.message('This work has no saved prompt to recreate');
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setRecreateRequest({
      text: prompt,
      title: item.title,
      flowId: item.flow_id,
      tag: item.tag,
      workId: item.id,
      kind: item.kind,
      params: item.recreate_params || null,
    });
  };

  const openVideoPreview = (payload, e) => {
    e?.stopPropagation?.();
    const url = String(payload?.url || '').trim();
    const poster = String(payload?.poster || '').trim();
    if (!url && !poster) {
      toast.message('No video to view yet');
      return;
    }
    setVideoPreview({
      title: payload?.title || 'Video',
      url,
      poster,
    });
  };

  const openYoutubePublish = (task, e) => {
    e?.stopPropagation?.();
    if (task?.youtube_posted && task?.youtube_url) {
      window.open(task.youtube_url, '_blank', 'noopener,noreferrer');
      return;
    }
    const url = getTaskExportedVideo(task);
    if (!url) {
      toast.message('Export a video first, then post to YouTube');
      return;
    }
    setYoutubePublish({
      videoUrl: url,
      title: task.title || '',
      scriptContext: String(task.prompt || '').slice(0, 4000),
      storyLanguage: task.language || '',
      storyId: task.kind === 'story' ? task.id : null,
      narrationProjectId: task.kind === 'narration' ? task.id : null,
      defaultShortsBoost: String(task.aspect_ratio || '').includes('9:16') || String(task.aspect_ratio || '').includes('9 / 16'),
    });
  };

  const filteredFlows = STORY_FLOWS.filter((flow) => {
    if (flow.hidden) return false;
    if (unifiedOnly && flow.id !== 'unified_story') return false;
    const cat = FLOW_CATEGORY_MAP[flow.id] || 'stories';
    const matchesCategory = activeFlowCategory === 'all' || cat === activeFlowCategory;
    const q = searchQuery.toLowerCase().trim();
    if (!q) return matchesCategory;
    const matchesSearch =
      flow.title.toLowerCase().includes(q) ||
      (flow.shortTitle && flow.shortTitle.toLowerCase().includes(q)) ||
      (flow.subtitle && flow.subtitle.toLowerCase().includes(q)) ||
      (flow.description && flow.description.toLowerCase().includes(q)) ||
      (flow.prompts && flow.prompts.some((p) => p.toLowerCase().includes(q)));
    return matchesCategory && matchesSearch;
  });

  const activeGalleryItems = galleryItems;
    return (
    <>
        {/* Top Header Bar */}
        <header className="h-14 bg-transparent px-3 sm:px-8 flex items-center justify-between sticky top-0 z-20 font-jakarta border-none gap-2">
          {/* Logo & Navigation Tabs */}
          <div className="flex items-center gap-3 sm:gap-8 min-w-0">
            {/* Brand Logo (Matching Landing Page) */}
            <Link
              to="/"
              className="flex items-center shrink-0 hover:opacity-90 transition-opacity cursor-pointer"
              title="Twinnee Home"
            >
              <TwinneeWordmark className="h-7 sm:h-8" />
            </Link>

            <nav className="hidden sm:flex items-center gap-7 text-xs font-medium">
              <button
                onClick={() => {
                  setActiveHeaderTab('gallery');
                  setActiveNav('home');
                }}
                className={`py-1 transition-all cursor-pointer ${activeHeaderTab === 'gallery'
                    ? 'text-white border-b-2 border-[#2DD4BF] font-bold'
                    : 'text-[#8a94a6] hover:text-gray-200'
                  }`}
              >
                Gallery
              </button>
            </nav>
          </div>

          {/* Right Action Cluster */}
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            {/* + Create New Gradient Button */}
            <button
              onClick={() => navigate('/dashboard?agent=new')}
              className="px-2.5 sm:px-4 py-2 rounded-full bg-gradient-to-r from-[#c4b5fd] via-[#a78bfa] to-[#67e8f9] text-[#080d14] font-bold text-xs tracking-wide hover:brightness-110 active:scale-95 transition-all shadow-md shadow-[#2DD4BF]/20 cursor-pointer flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5 stroke-[3]" />
              <span className="hidden sm:inline">Create New</span>
            </button>

            {/* Notification Bell */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors cursor-pointer relative ${showNotifications
                    ? 'border-[#2DD4BF] bg-[#2DD4BF]/10 text-[#2DD4BF]'
                    : 'border-[#262f3d] bg-[#161a22] text-[#8a94a6] hover:text-white hover:border-gray-500'
                  }`}
              >
                <Bell className="w-3.5 h-3.5" />
                {notifications.filter((n) => !n.read).length > 0 && (
                  <span className="w-2 h-2 rounded-full bg-[#2DD4BF] absolute top-1.5 right-1.5 ring-2 ring-[#0d1016]" />
                )}
              </button>

              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.95 }}
                    className="absolute right-0 mt-2 w-[min(20rem,calc(100vw-1.5rem))] rounded-2xl border border-[#262f3d] bg-[#0d1016]/95 backdrop-blur-xl shadow-2xl overflow-hidden z-50 font-jakarta"
                  >
                    <div className="p-3.5 border-b border-[#262f3d] flex items-center justify-between">
                      <h3 className="text-xs font-bold text-white">Notifications</h3>
                      <span className="text-[10px] font-mono text-gray-400">
                        {notifications.filter((n) => !n.read).length} new
                      </span>
                    </div>
                    <div className="max-h-80 overflow-y-auto divide-y divide-[#1b212d]">
                      {notifications.length === 0 ? (
                        <div className="p-5 text-center text-xs text-gray-400">
                          No new notifications
                        </div>
                      ) : (
                        notifications.map((notif) => (
                          <button
                            key={notif.id}
                            type="button"
                            className="w-full text-left p-3.5 hover:bg-white/[0.04] transition-colors cursor-pointer"
                            onClick={() => {
                              setShowNotifications(false);
                              const target = notif.link || '/tasks';
                              if (target.startsWith('http')) {
                                window.location.href = target;
                              } else {
                                navigate(target.startsWith('/') ? target : `/tasks?job=${notif.job_id || ''}`);
                              }
                            }}
                          >
                            <p className="text-xs font-semibold text-white">{notif.title}</p>
                            <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">{notif.message}</p>
                          </button>
                        ))
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Settings */}
            <button
              onClick={() => navigate('/characters')}
              className="hidden sm:flex w-8 h-8 rounded-full bg-[#161a22] border border-[#262f3d] hover:border-gray-500 items-center justify-center text-[#8a94a6] hover:text-white transition-colors cursor-pointer"
            >
              <Settings className="w-3.5 h-3.5" />
            </button>

            {/* Profile Avatar */}
            <div
              onClick={() => navigate('/pricing')}
              className="w-8 h-8 rounded-full bg-gradient-to-tr from-[#8B5CF6] to-[#2DD4BF] p-[1.5px] cursor-pointer"
              title={displayName}
            >
              <div className="w-full h-full rounded-full bg-[#161a22] flex items-center justify-center overflow-hidden">
                <span className="text-xs font-bold text-white">
                  {displayName[0]}
                </span>
              </div>
            </div>
            {!channelHome.loading && !channelHome.data?.connected ? (
              <button
                type="button"
                onClick={channelHome.connect}
                disabled={channelHome.connectBusy}
                className="px-2.5 sm:px-4 py-2 rounded-full bg-[#34d399] text-[#06211b] font-bold text-xs tracking-wide hover:brightness-110 active:scale-95 transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-60"
                data-testid="dashboard-connect-youtube"
              >
                <Youtube className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{channelHome.connectBusy ? 'Opening…' : 'Connect YouTube'}</span>
                <span className="sm:hidden">{channelHome.connectBusy ? '…' : 'YouTube'}</span>
              </button>
            ) : null}
            {channelHome.data?.connected ? (
              <div className="flex items-center gap-1 min-w-0 max-w-[42vw] sm:max-w-[220px]" data-testid="dashboard-youtube-channel">
                <span className="truncate text-xs font-semibold text-white" title={channelHome.data.channel_title || 'YouTube'}>
                  {channelHome.data.channel_title || 'YouTube'}
                </span>
                <button
                  type="button"
                  onClick={channelHome.disconnect}
                  disabled={channelHome.disconnectBusy}
                  title="Disconnect YouTube"
                  aria-label="Disconnect YouTube"
                  className="w-8 h-8 shrink-0 rounded-full border border-[#262f3d] bg-[#161a22] text-[#8a94a6] hover:text-white hover:border-gray-500 inline-flex items-center justify-center disabled:opacity-60"
                  data-testid="dashboard-youtube-disconnect"
                >
                  {channelHome.disconnectBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
                </button>
              </div>
            ) : null}
          </div>
        </header>

        {/* Dashboard Scrollable Body */}
        <main
          className="px-3 sm:px-8 py-5 space-y-5 flex-1 w-full max-w-none font-jakarta overflow-x-hidden"
        >
          {/* ─────────────────────────────────────────
              CREATE STORY HERO (prompt / voice / MP3)
          ───────────────────────────────────────── */}
          <DashboardCreateStoryHero
            onTaskCreated={() => loadData()}
            recreateRequest={recreateRequest}
            onRecreateConsumed={() => setRecreateRequest(null)}
          >
            <WhatToMakeNext data={channelHome.data} loading={channelHome.loading} />
          </DashboardCreateStoryHero>
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="dashboard-seo-actions">
            <button
              type="button"
              onClick={() => navigate('/social-publish/auto')}
              className="group flex items-center gap-4 rounded-[22px] px-5 py-5 text-left bg-gradient-to-r from-[#c4b5fd] via-[#a78bfa] to-[#67e8f9] text-[#080d14] shadow-lg shadow-[#a78bfa]/25 hover:brightness-110 active:scale-[0.99] transition-all cursor-pointer min-h-[92px]"
              data-testid="automate-all-button"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-black/15">
                <Sparkles className="w-6 h-6" />
              </span>
              <span className="min-w-0">
                <span className="block text-lg sm:text-xl font-extrabold tracking-tight">Automate All</span>
                <span className="block text-sm font-semibold text-[#080d14]/75">SEO + publish</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => navigate(advancedUnlocked ? '/channel/advanced' : '/pricing?unlock=analytics')}
              className="group flex items-center gap-4 rounded-[22px] px-5 py-5 text-left border border-amber-300/40 bg-[#1a160d] text-white shadow-lg shadow-amber-400/10 hover:border-amber-200/70 hover:bg-[#241c10] active:scale-[0.99] transition-all cursor-pointer min-h-[92px]"
              data-testid="unlock-advanced-analytics"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-300/15 text-amber-200">
                {advancedUnlocked ? <Crown className="w-6 h-6" /> : <Lock className="w-6 h-6" />}
              </span>
              <span className="min-w-0">
                <span className="block text-lg sm:text-xl font-extrabold tracking-tight">
                  {advancedUnlocked ? 'Advanced Analytics' : 'Unlock Advanced Analytics'}
                </span>
                <span className="block text-sm text-amber-100/70">
                  {advancedUnlocked ? 'Compliance, SEO, and ranking' : 'Unlocks after any payment'}
                </span>
              </span>
            </button>
          </section>
          <YouTubeHighlights
            data={channelHome.data}
            loading={channelHome.loading}
            connect={channelHome.connect}
            connectBusy={channelHome.connectBusy}
            contentFormat={youtubeFormat}
            onContentFormat={setYoutubeFormat}
          />

          <GeneralTrendsBrief />

          {/* ─────────────────────────────────────────
              GALLERY FILTER TABS & SEARCH BAR
          ───────────────────────────────────────── */}
          <section className="space-y-4 pt-1">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              {/* Category Pills */}
              <div className="flex flex-wrap items-center gap-1.5 scrollbar-none">
                {GALLERY_TABS.map((tab) => {
                  const isSelected = activeGalleryTab === tab.id;
                  if (tab.isGradient) {
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveGalleryTab(tab.id)}
                        className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 shadow-md shadow-[#a78bfa]/25 ${isSelected
                            ? 'bg-gradient-to-r from-[#c4b5fd] via-[#a78bfa] to-[#67e8f9] text-[#080d14] brightness-110 scale-[1.03] ring-2 ring-[#67e8f9]/50'
                            : 'bg-gradient-to-r from-[#c4b5fd] via-[#a78bfa] to-[#67e8f9] text-[#080d14] hover:brightness-110 active:scale-95'
                        }`}
                      >
                        <FolderKanban className="w-3.5 h-3.5" />
                        <span>{tab.label}</span>
                        {tasks.length > 0 && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded-full font-mono bg-black/25 text-[#080d14] font-extrabold"
                          >
                            {tasks.length}
                          </span>
                        )}
                      </button>
                    );
                  }

                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveGalleryTab(tab.id)}
                      className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${isSelected
                          ? 'bg-[#13322d] text-[#34d399] border border-[#214f48]'
                          : 'text-[#7d8799] hover:text-white bg-[#12151d] border border-[#1f2635] hover:border-gray-600'
                        }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Search & Publish Actions */}
              <div className="flex items-center gap-3 min-w-0 w-full md:w-auto">
                <div className="relative flex-1 md:flex-none min-w-0">
                  <Search className="w-3.5 h-3.5 text-gray-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search inspiration & flows..."
                    className="h-9 pl-9 pr-4 rounded-full bg-[#12151d] border border-[#1f2635] text-white text-xs placeholder:text-[#5a6475] focus:border-[#2DD4BF]/50 focus:outline-none transition-colors w-full sm:w-60"
                  />
                </div>

                <button
                  onClick={() => navigate('/social-publish')}
                  className="hidden sm:inline-flex px-5 py-2 rounded-full border border-[#2b3547] bg-[#1b212d] hover:bg-[#232b3b] text-xs font-semibold text-white transition-colors cursor-pointer"
                >
                  Publish
                </button>
              </div>
            </div>

            {/* When Your Projects tab is active — same staggered masonry as gallery tabs */}
            {activeGalleryTab === 'your_projects' ? (
              loading ? (
                <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 gap-x-3 pt-1 pb-16 [column-fill:_balance]">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <div
                      key={n}
                      className="break-inside-avoid mb-3 rounded-[18px] overflow-hidden border border-[#1f2635] bg-[#12151d] animate-pulse"
                      style={{ aspectRatio: inferProjectAspect({ id: String(n) }) }}
                    />
                  ))}
                </div>
              ) : tasks.length > 0 ? (
                <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 gap-x-3 pt-1 pb-16 [column-fill:_balance]">
                    {tasks.map((task) => {
                      const preview = getTaskPreview(task);
                    const exportedVideo = getTaskExportedVideo(task);
                      const statusBadge = getStatusBadge(task.status);
                      const previewPayload = {
                        url: exportedVideo || (preview?.type === 'video' ? preview.url : ''),
                        title: task.title || 'Untitled Project',
                        poster: preview?.type === 'image' ? preview.url : '',
                      };

                      return (
                        <div
                          key={`${task.kind}-${task.id}`}
                        className="relative group break-inside-avoid mb-3 z-10 hover:z-50"
                        onMouseEnter={(e) => startHoverVideo(e.currentTarget)}
                        onMouseLeave={(e) => stopHoverVideo(e.currentTarget)}
                      >
                        <div
                          data-project-card
                          onClick={() => openVideoPreview(previewPayload)}
                          className="w-full rounded-[16px] sm:rounded-[18px] overflow-hidden relative bg-[#12151d] shadow-lg border border-[#1f2635] group-hover:border-[#2DD4BF]/50 transition-all duration-200 cursor-pointer"
                          style={{
                            aspectRatio: galleryAspectCss(
                              task.aspect_ratio ||
                                task.aspectRatio ||
                                inferProjectAspect(task)
                            ),
                          }}
                        >
                            {preview?.type === 'image' ? (
                              <img
                                src={preview.url}
                                alt={task.title}
                              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              onLoad={(e) => {
                                const img = e.currentTarget;
                                if (!img.naturalWidth || !img.naturalHeight) return;
                                if (task.aspect_ratio || task.aspectRatio) return;
                                const parent = img.closest('[data-project-card]');
                                if (parent) {
                                  parent.style.aspectRatio = `${img.naturalWidth} / ${img.naturalHeight}`;
                                }
                              }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : preview?.type === 'video' ? (
                              <video
                                src={preview.url}
                                muted
                              loop
                                playsInline
                              preload="metadata"
                              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              onLoadedMetadata={(e) => {
                                const v = e.currentTarget;
                                if (!v.videoWidth || !v.videoHeight) return;
                                if (task.aspect_ratio || task.aspectRatio) return;
                                const parent = v.closest('[data-project-card]');
                                if (parent) {
                                  parent.style.aspectRatio = `${v.videoWidth} / ${v.videoHeight}`;
                                }
                                }}
                              />
                            ) : (
                            <div className="absolute inset-0 flex items-center justify-center bg-[#161a22]">
                                <Film className="w-7 h-7 text-gray-600 group-hover:text-[#2DD4BF] transition-colors" />
                              </div>
                            )}

                          {exportedVideo ? (
                            <video
                              data-hover-preview
                              data-src={exportedVideo}
                              muted
                              loop
                              playsInline
                              preload="none"
                              className="absolute inset-0 z-[5] w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
                            />
                          ) : null}

                          <div className="absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-black/60 to-transparent pointer-events-none z-10" />
                          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 p-2.5 pt-10 bg-gradient-to-t from-black/90 via-black/45 to-transparent">
                            <h4 className="text-xs font-bold text-white truncate group-hover:text-[#2DD4BF] transition-colors">
                              {task.title || 'Untitled Project'}
                            </h4>
                            <p className="text-[10px] text-gray-400 truncate mt-0.5 capitalize">
                              {task.kind || 'Story'}
                              {typeof task.scene_count === 'number' && task.scene_count > 0
                                ? ` · ${task.scene_count} ${task.kind === 'narration' ? 'segments' : 'scenes'}`
                                : ''}
                              {exportedVideo ? ' · Video' : ''}
                            </p>
                            <div className="pointer-events-auto mt-2 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                title="View video"
                                data-testid={`project-view-${task.id}`}
                                onClick={(e) => openVideoPreview(previewPayload, e)}
                                className="flex-1 h-7 rounded-lg bg-white/15 hover:bg-white/25 border border-white/15 text-white text-[11px] font-semibold inline-flex items-center justify-center gap-1 cursor-pointer"
                              >
                                <Eye className="w-3 h-3" />
                                View
                              </button>
                              <button
                                type="button"
                                title="Recreate in Create agent"
                                data-testid={`project-recreate-${task.id}`}
                                onClick={(e) => openRecreate(task, e)}
                                className={RECREATE_BUTTON_CLASS}
                              >
                                <RotateCcw className="w-3 h-3" />
                                Recreate
                              </button>
                              <button
                                type="button"
                                onClick={(e) => deleteProject(task, e)}
                                title="Delete project"
                                className="w-7 h-7 rounded-lg text-gray-300 hover:text-red-400 hover:bg-white/[0.08] border border-white/10 transition-colors cursor-pointer shrink-0 inline-flex items-center justify-center"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          <div className="absolute top-2 left-2 z-20 flex items-center gap-1.5">
                            {task.status ? (
                              <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium border backdrop-blur-sm ${statusBadge.bg}`}>
                                {statusBadge.label}
                              </span>
                            ) : null}
                          </div>
                          {!task.youtube_posted && exportedVideo ? (
                            <div
                              className="absolute top-2 right-2 z-30"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                title="Post to YouTube"
                                data-testid={`project-youtube-${task.id}`}
                                onClick={(e) => openYoutubePublish(task, e)}
                                className="w-7 h-7 rounded-lg bg-black/60 hover:bg-[#FF0000]/90 border border-white/15 text-white flex items-center justify-center backdrop-blur-sm cursor-pointer"
                              >
                                <Youtube className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : null}
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="rounded-[18px] border border-dashed border-[#1f2635] p-10 flex flex-col items-center justify-center text-center bg-[#12151d]">
                  <FolderKanban className="w-9 h-9 text-gray-600 mb-3" />
                  <h4 className="text-sm font-bold text-white mb-1">No Projects Found</h4>
                  <p className="text-xs text-gray-400 mb-4 max-w-sm">
                    You have not created any projects yet. Choose a creation flow above to get started!
                  </p>
                  <button
                    onClick={() => navigate('/flows')}
                    className="px-4 py-2 rounded-full bg-[#2DD4BF] hover:bg-[#26bba8] text-xs font-bold text-black transition-colors cursor-pointer"
                  >
                    Start First Creation
                  </button>
                </div>
              )
            ) : galleryLoading ? (
              <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 gap-x-3 pt-1 pb-16 [column-fill:_balance]">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <div
                    key={n}
                    className="break-inside-avoid mb-3 rounded-[18px] overflow-hidden border border-[#1f2635] bg-[#12151d] animate-pulse"
                    style={{ aspectRatio: inferProjectAspect({ id: String(n) }) }}
                  />
                ))}
              </div>
            ) : activeGalleryItems.length > 0 ? (
              <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 gap-x-3 pt-1 pb-16 [column-fill:_balance]">
                {activeGalleryItems.map((item) => (
                  <div
                    key={`${item.kind}-${item.id}`}
                    className="relative group break-inside-avoid mb-3 z-10 hover:z-50"
                    onMouseEnter={(e) => startHoverVideo(e.currentTarget)}
                    onMouseLeave={(e) => stopHoverVideo(e.currentTarget)}
                  >
                    {/* Base Card Frame — height follows media aspect ratio */}
                    <div
                      data-gallery-card
                      onClick={() =>
                        openVideoPreview({
                          url: item.video_url,
                          title: item.title,
                          poster: item.image_url,
                        })
                      }
                      className="w-full rounded-[16px] sm:rounded-[18px] overflow-hidden relative bg-[#12151d] shadow-lg border border-[#1f2635] group-hover:border-white/20 transition-all duration-200 cursor-pointer"
                      style={{
                        aspectRatio: galleryAspectCss(
                          item.aspect_ratio ||
                            item.aspectRatio ||
                            inferGalleryAspect(item, activeGalleryTab)
                        ),
                      }}
                    >
                      {/* Poster: segment/scene still, else first frame of video */}
                      {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.title}
                          className="absolute inset-0 w-full h-full object-cover"
                          onLoad={(e) => {
                            const img = e.currentTarget;
                            if (!img.naturalWidth || !img.naturalHeight) return;
                            const parent = img.closest('[data-gallery-card]');
                            if (parent && !item.aspect_ratio && !item.aspectRatio) {
                              parent.style.aspectRatio = `${img.naturalWidth} / ${img.naturalHeight}`;
                            }
                          }}
                        onError={(e) => {
                            // Drop broken still — video poster (if any) can take over
                            e.currentTarget.style.display = 'none';
                        }}
                      />
                      ) : null}

                      {item.video_url ? (
                        <video
                          data-hover-preview
                          data-src={item.video_url}
                          // No still → show video frame as thumbnail immediately
                          {...(item.image_url
                            ? { preload: 'none' }
                            : { src: item.video_url, preload: 'metadata' })}
                          muted
                          loop
                          playsInline
                          className={`absolute inset-0 z-[5] w-full h-full object-cover pointer-events-none transition-opacity duration-200 ${
                            item.image_url
                              ? 'opacity-0 group-hover:opacity-100'
                              : 'opacity-100'
                          }`}
                          onLoadedMetadata={(e) => {
                            const v = e.currentTarget;
                            if (!v.videoWidth || !v.videoHeight) return;
                            if (item.aspect_ratio || item.aspectRatio) return;
                            const parent = v.closest('[data-gallery-card]');
                            if (parent) {
                              parent.style.aspectRatio = `${v.videoWidth} / ${v.videoHeight}`;
                            }
                          }}
                        />
                      ) : !item.image_url ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-[#161a22]">
                          <Film className="w-7 h-7 text-gray-600" />
                        </div>
                      ) : null}

                      {/* Top dark subtle vignette */}
                      <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/60 to-transparent pointer-events-none z-10" />

                      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 p-2.5 pt-10">
                        <div
                          aria-hidden
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            background:
                              'linear-gradient(to top, rgba(8,10,14,0.88) 0%, rgba(8,10,14,0.45) 55%, transparent 100%)',
                          }}
                        />
                        <div className="pointer-events-auto relative flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            title="View video"
                            data-testid={`gallery-view-${item.kind}-${item.id}`}
                            onClick={(e) =>
                              openVideoPreview(
                                {
                                  url: item.video_url,
                                  title: item.title,
                                  poster: item.image_url,
                                },
                                e
                              )
                            }
                            className="flex-1 h-7 rounded-lg bg-white/15 hover:bg-white/25 border border-white/15 text-white text-[11px] font-semibold inline-flex items-center justify-center gap-1 cursor-pointer"
                          >
                            <Eye className="w-3 h-3" />
                            View
                          </button>
                          <button
                            type="button"
                            title="Recreate in Create agent"
                            data-testid={`gallery-recreate-${item.kind}-${item.id}`}
                            onClick={(e) => openRecreate(item, e)}
                            className={RECREATE_BUTTON_CLASS}
                          >
                            <RotateCcw className="w-3 h-3" />
                            Recreate
                          </button>
                        </div>
                        <div className="pointer-events-auto relative mt-1.5 flex items-center justify-between text-white/90" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1.5 min-w-0 pr-1">
                            <div
                              className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold text-white uppercase shrink-0 ring-1 ring-white/20"
                              style={{ backgroundColor: item.creator?.color || '#2DD4BF' }}
                            >
                              {item.creator?.avatar || item.creator?.name?.[0] || 'U'}
                            </div>
                            <span className="text-[11px] font-medium text-white/95 truncate">
                              {item.creator?.name}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => toggleGalleryLike(item, e)}
                            disabled={likingKey === `${item.kind}:${item.id}` || item.is_owner}
                            className="flex items-center gap-1 text-[11px] text-white/80 shrink-0 cursor-pointer disabled:opacity-50"
                            title={item.liked ? 'Unlike' : 'Like'}
                          >
                            <Heart
                              className={`w-3 h-3 ${item.liked ? 'fill-red-400 text-red-400' : 'text-white/75'}`}
                            />
                            <span>{item.likes_count}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-[18px] border border-dashed border-[#1f2635] p-10 flex flex-col items-center justify-center text-center bg-[#12151d]">
                <Search className="w-8 h-8 text-gray-600 mb-2" />
                <h4 className="text-sm font-bold text-white mb-1">No creator works yet</h4>
                <p className="text-xs text-gray-400 max-w-sm">
                  {activeGalleryTab === 'follows'
                    ? 'Follow other creators, or switch tabs to see community projects with real media.'
                    : 'When other creators finish projects, their work appears here. Your own projects stay under Your Projects.'}
                </p>
              </div>
            )}
          </section>
        </main>
        <Dialog open={Boolean(videoPreview)} onOpenChange={(open) => { if (!open) setVideoPreview(null); }}>
          <DialogContent className="max-w-4xl gap-3 border-white/10 bg-[#0c1018] p-3 sm:p-4 text-white sm:rounded-2xl [&>button]:text-white">
            <DialogTitle className="pr-8 text-sm sm:text-base font-semibold truncate">
              {videoPreview?.title || 'Video'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Video playback on this page.
            </DialogDescription>
            <div className="overflow-hidden rounded-xl bg-black">
              {videoPreview?.url ? (
                <video
                  key={videoPreview.url}
                  src={videoPreview.url}
                  poster={videoPreview.poster || undefined}
                  controls
                  autoPlay
                  playsInline
                  className="mx-auto max-h-[75vh] w-full bg-black object-contain"
                />
              ) : videoPreview?.poster ? (
                <img
                  src={videoPreview.poster}
                  alt={videoPreview.title || 'Preview'}
                  className="mx-auto max-h-[75vh] w-full object-contain"
                />
              ) : null}
            </div>
          </DialogContent>
        </Dialog>
        <YouTubePublishDialog
          open={Boolean(youtubePublish)}
          onOpenChange={(open) => { if (!open) setYoutubePublish(null); }}
          oauthReturnPath="/dashboard"
          defaultVideoUrl={youtubePublish?.videoUrl || ''}
          defaultTitle={youtubePublish?.title || ''}
          scriptContext={youtubePublish?.scriptContext || ''}
          storyLanguage={youtubePublish?.storyLanguage || ''}
          narrationProjectId={youtubePublish?.narrationProjectId || null}
          storyId={youtubePublish?.storyId || null}
          defaultShortsBoost={Boolean(youtubePublish?.defaultShortsBoost)}
          onPublished={() => {
            setYoutubePublish(null);
            loadData();
            toast.success('Posted to YouTube');
          }}
        />
    </>
  );
};

export default DashboardPage;
